export enum ClientRole {
  PERSONAL = 'PERSONAL_CLIENT',
  CORPORATE_OWNER = 'CORPORATE_OWNER',
  CORPORATE_USER = 'CORPORATE_USER',
}

export enum KycStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING_REVIEW = 'PENDING_REVIEW',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
}

export enum AccountType {
  PERSONAL = 'personal',
  CORPORATE = 'corporate',
}

export enum AccountStatus {
  PENDING_KYC = 'pending_kyc',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  CLOSED = 'closed',
}

export enum TransactionStatus {
  INITIATED = 'initiated',
  PENDING = 'pending',
  BOOKED = 'booked',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
  FAILED = 'failed',
  REVERSED = 'reversed',
}

export enum TransactionType {
  CREDIT = 'credit',
  DEBIT = 'debit',
}

export enum AssetType {
  FIAT = 'fiat',
  CRYPTO = 'crypto',
}

export enum CryptoAsset {
  USDT = 'USDT',
  USDC = 'USDC',
  BTC = 'BTC',
  BNB = 'BNB',
  ETH = 'ETH',
}

export enum CryptoNetwork {
  ERC20 = 'ERC-20',
  BEP20 = 'BEP-20',
  BITCOIN_MAINNET = 'Bitcoin Mainnet',
}

// Multi-currency (2026-05-24) — extended from EUR-only to EUR + USD.
export enum FiatCurrency {
  EUR = 'EUR',
  USD = 'USD',
}

export enum BeneficiaryType {
  INDIVIDUAL = 'individual',
  COMPANY = 'company',
}

export enum TransferType {
  SEPA = 'SEPA',
  SWIFT = 'SWIFT',
  ON_CHAIN = 'ON_CHAIN',
  FX_CONVERSION = 'FX_CONVERSION',
}

export enum DepositType {
  FIAT = 'FIAT',
  CRYPTO = 'CRYPTO',
}

export enum WithdrawalType {
  FIAT = 'FIAT',
  CRYPTO = 'CRYPTO',
}

export enum ApprovalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum LedgerOperationType {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
  TRANSFER = 'transfer',
  CONVERSION = 'conversion',
  FEE = 'fee',
  ADJUSTMENT = 'adjustment',
  OTHER = 'other',
}

/**
 * Primary source of funds / wealth — mirrors the Prisma enum
 * `PrimarySourceOfFunds` defined in
 * `normax-trust-banking-backend/prisma/schema.prisma`. Values are sent
 * verbatim to `POST /auth/register/personal` and
 * `POST /auth/register/corporate`; the backend rejects unknown values
 * with 400 via `@IsEnum(PrimarySourceOfFunds)`.
 */
export enum PrimarySourceOfFunds {
  SALARY = 'SALARY',
  BUSINESS_REVENUE = 'BUSINESS_REVENUE',
  INVESTMENTS = 'INVESTMENTS',
  INHERITANCE = 'INHERITANCE',
  GIFT = 'GIFT',
  OTHER = 'OTHER',
}

/**
 * CB — Client Blocking (2026-06-30, AD-049 / CB-LD-1).
 *
 * Mirrors the backend `AuthUserDto.blockStatus` field. Type alias (not a
 * runtime enum) — the backend sends these as plain lowercase strings on
 * `/auth/me`, login, and verify-2fa responses. `'none'` for unblocked
 * accounts. Three blocking levels:
 *   - `blocked`           Full lockout. Login is refused with 401
 *                         ACCOUNT_BLOCKED before a session is issued.
 *   - `partially_blocked` Login allowed; all non-KYC routes are gated by
 *                         the consent modal. KYC routes remain reachable.
 *   - `transfer_withdraw_blocked` Login allowed; only the transfer and
 *                         withdrawal route groups are gated.
 */
export type ClientBlockStatus =
  | 'none'
  | 'blocked'
  | 'partially_blocked'
  | 'transfer_withdraw_blocked';
